import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, ButtonAdorned, Subtitle } from '@components/atoms';
import PropTypes from 'prop-types';
import {
  LinearProgress,
  Grid,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  InputAdornment,
  Typography,
  Divider,
  Chip,
  makeStyles,
} from '@material-ui/core';
import { useEnsayos } from '@hooks';
import {
  Clear as ClearIcon,
  Search as SearchIcon,
  DeleteForeverTwoTone as DeleteForeverTwoToneIcon,
  EditTwoTone as EditTwoToneIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import AddEnsayos from './addEnsayosModal';

const SEARCH_FIELDS = ['material', 'ensayo', 'metodo'];

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 420,
  },
  searchField: {
    marginBottom: theme.spacing(1),
  },
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    height: 360,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  panelList: {
    overflow: 'auto',
    flex: 1,
    padding: 0,
  },
  listItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  selectedChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  secondaryActions: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const normalizeEnsayo = (item) => {
  const id = item.id || item._id;
  return {
    ...item,
    id,
    material: item.material || '',
    ensayo: item.ensayo || '',
    metodo: item.metodo || '',
  };
};

const formatEnsayo = ({
  id, material, ensayo, metodo,
}) => ({
  id,
  material,
  ensayo,
  metodo,
});

const matchesSearch = (row, searchValue) => {
  if (!searchValue.trim()) return true;
  const term = searchValue.trim().toLowerCase();
  return SEARCH_FIELDS.some((field) => String(row[field] || '').toLowerCase().includes(term));
};

const EnsayosModal = ({
  showDialog,
  setShowDialog,
  title,
  dataSelected = [],
  setDataSelected,
}) => {
  const classes = useStyles();
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [selectedMap, setSelectedMap] = useState({});
  const { getEnsayosAll, deleteEnsayo, isEnsayosLoading } = useEnsayos();
  const { enqueueSnackbar } = useSnackbar();
  const [openDialogAdd, setOpenDialogAdd] = useState(false);
  const [editEnsayo, setEditEnsayo] = useState({});
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [idSelected, setIdSelected] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const refresh = () => setRefreshKey((prev) => prev + 1);

  const selectedList = useMemo(() => Object.values(selectedMap), [selectedMap]);
  const selectedIds = useMemo(() => new Set(Object.keys(selectedMap)), [selectedMap]);

  const availableList = useMemo(
    () => data.filter((row) => matchesSearch(row, searchText) && !selectedIds.has(String(row.id))),
    [data, searchText, selectedIds],
  );

  const deleteOneEnsayo = async (id) => {
    try {
      const result = await deleteEnsayo(id);
      if (result) {
        enqueueSnackbar('Ensayo eliminado', { variant: 'success' });
        setSelectedMap((prev) => {
          const next = { ...prev };
          delete next[id];
          delete next[String(id)];
          return next;
        });
      }
    } catch (error) {
      enqueueSnackbar('Error eliminando ensayo', { variant: 'error' });
    } finally {
      setOpenConfirmDelete(false);
      refresh();
    }
  };

  const actionSelected = (action, dataEnsayo = null) => {
    if (action === 'edit') {
      setEditEnsayo({
        data: {
          id: dataEnsayo.id,
          material: dataEnsayo.material,
          ensayo: dataEnsayo.ensayo,
          metodo: dataEnsayo.metodo,
        },
        action: 'edit',
        refresh,
      });
    }
    if (action === 'add') {
      setEditEnsayo({
        data: {
          id: '',
          material: '',
          ensayo: '',
          metodo: '',
        },
        action: 'add',
        refresh,
      });
    }
    setOpenDialogAdd(true);
  };

  const toggleSelect = (ensayo) => {
    const key = String(ensayo.id);
    setSelectedMap((prev) => {
      if (prev[key]) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return {
        ...prev,
        [key]: formatEnsayo(ensayo),
      };
    });
  };

  const removeSelected = (id) => {
    setSelectedMap((prev) => {
      const next = { ...prev };
      delete next[String(id)];
      return next;
    });
  };

  const clearSearch = () => setSearchText('');

  const handleConfirm = () => {
    setDataSelected(selectedList.map(formatEnsayo));
    setShowDialog(false);
  };

  const handleClose = () => {
    setShowDialog(false);
  };

  useEffect(() => {
    if (!showDialog) return;

    const initialSelected = {};
    (dataSelected || []).forEach((item) => {
      const normalized = normalizeEnsayo(item);
      if (normalized.id) {
        initialSelected[String(normalized.id)] = formatEnsayo(normalized);
      }
    });
    setSelectedMap(initialSelected);
    setSearchText('');
  }, [showDialog]);

  useEffect(() => {
    if (!showDialog) return undefined;

    let cancelled = false;
    setLoaded(false);

    const fetchData = async () => {
      try {
        const response = await getEnsayosAll();
        if (cancelled) return;
        const list = Array.isArray(response) ? response : [];
        setData(list.map(normalizeEnsayo));
      } catch (error) {
        if (!cancelled) {
          enqueueSnackbar('Error obteniendo datos', { variant: 'error' });
          setData([]);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [showDialog, refreshKey]);

  return (
    <>
      {openDialogAdd && (
        <AddEnsayos
          title={editEnsayo.action === 'edit' ? 'Editar Ensayo' : 'Nuevo Ensayo'}
          setShowDialog={setOpenDialogAdd}
          showDialog={openDialogAdd}
          {...editEnsayo}
        />
      )}
      {openConfirmDelete && (
        <Dialog
          open={openConfirmDelete}
          title="Eliminar Ensayo"
          onClose={() => setOpenConfirmDelete(false)}
          content={(
            <Grid container spacing={2} style={{ textAlign: 'center' }}>
              <Grid item xs={12}>
                <p>¿Está seguro que desea eliminar el ensayo?</p>
              </Grid>
              <Grid item xs={6}>
                <ButtonAdorned
                  onClick={() => deleteOneEnsayo(idSelected)}
                  color="secondary"
                  disabled={isEnsayosLoading}
                  loading={isEnsayosLoading}
                >
                  Eliminar
                </ButtonAdorned>
              </Grid>
              <Grid item xs={6}>
                <ButtonAdorned
                  onClick={() => setOpenConfirmDelete(false)}
                  color="primary"
                  disabled={isEnsayosLoading}
                >
                  Cancelar
                </ButtonAdorned>
              </Grid>
            </Grid>
          )}
        />
      )}
      <Dialog
        open={showDialog}
        title={title}
        fullWidth
        size="md"
        onClose={handleClose}
        actions={[
          {
            label: 'Nuevo',
            onClick: () => actionSelected('add'),
            color: 'secondary',
            disabled: isEnsayosLoading,
          },
          {
            label: 'Cancelar',
            onClick: handleClose,
            color: 'default',
            disabled: isEnsayosLoading,
          },
          {
            label: `Confirmar (${selectedList.length})`,
            onClick: handleConfirm,
            color: 'primary',
            disabled: isEnsayosLoading,
          },
        ]}
        content={(
          <Grid container spacing={2} className={classes.root}>
            {!loaded ? (
              <Grid item xs={12}>
                <LinearProgress />
              </Grid>
            ) : (
              <>
                {isEnsayosLoading && (
                  <Grid item xs={12}>
                    <LinearProgress />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                    placeholder="Buscar por material, ensayo o método..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: searchText ? (
                        <InputAdornment position="end">
                          <IconButton
                            title="Limpiar"
                            aria-label="Limpiar búsqueda"
                            size="small"
                            onClick={clearSearch}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div className={classes.panel}>
                    <div className={classes.panelHeader}>
                      <Typography variant="subtitle2">
                        Disponibles
                      </Typography>
                      <Chip size="small" label={availableList.length} />
                    </div>
                    <List dense className={classes.panelList}>
                      {availableList.length === 0 ? (
                        <div className={classes.emptyState}>
                          {data.length === 0
                            ? 'No hay ensayos registrados. Crea uno con "Nuevo".'
                            : 'No hay resultados para la búsqueda.'}
                        </div>
                      ) : (
                        availableList.map((ensayo) => (
                          <ListItem
                            key={ensayo.id}
                            button
                            className={classes.listItem}
                            onClick={() => toggleSelect(ensayo)}
                          >
                            <ListItemIcon>
                              <AddIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={ensayo.ensayo}
                              secondary={`${ensayo.material} · ${ensayo.metodo}`}
                            />
                            <ListItemSecondaryAction>
                              <div className={classes.secondaryActions}>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  title="Editar"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    actionSelected('edit', ensayo);
                                  }}
                                >
                                  <EditTwoToneIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  edge="end"
                                  size="small"
                                  title="Eliminar"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setIdSelected(ensayo.id);
                                    setOpenConfirmDelete(true);
                                  }}
                                >
                                  <DeleteForeverTwoToneIcon fontSize="small" color="secondary" />
                                </IconButton>
                              </div>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))
                      )}
                    </List>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <div className={classes.panel}>
                    <div className={classes.panelHeader}>
                      <Typography variant="subtitle2">
                        Seleccionados
                      </Typography>
                      <Chip size="small" color="primary" label={selectedList.length} />
                    </div>
                    <List dense className={classes.panelList}>
                      {selectedList.length === 0 ? (
                        <div className={classes.emptyState}>
                          Selecciona ensayos de la lista disponible.
                        </div>
                      ) : (
                        selectedList.map((ensayo) => (
                          <ListItem key={ensayo.id} className={classes.listItem}>
                            <ListItemIcon>
                              <Checkbox
                                edge="start"
                                checked
                                tabIndex={-1}
                                disableRipple
                                color="primary"
                                onChange={() => removeSelected(ensayo.id)}
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={ensayo.ensayo}
                              secondary={`${ensayo.material} · ${ensayo.metodo}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                title="Quitar"
                                onClick={() => removeSelected(ensayo.id)}
                              >
                                <RemoveIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))
                      )}
                    </List>
                  </div>
                </Grid>

                {selectedList.length > 0 && (
                  <Grid item xs={12}>
                    <Divider />
                    <Subtitle>Resumen de selección</Subtitle>
                    <div className={classes.selectedChips}>
                      {selectedList.map((ensayo) => (
                        <Chip
                          key={ensayo.id}
                          size="small"
                          label={`${ensayo.material} · ${ensayo.ensayo}`}
                          onDelete={() => removeSelected(ensayo.id)}
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </div>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        )}
      />
    </>
  );
};

EnsayosModal.propTypes = {
  showDialog: PropTypes.bool.isRequired,
  setShowDialog: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  dataSelected: PropTypes.arrayOf(PropTypes.shape({})),
  setDataSelected: PropTypes.func,
};

EnsayosModal.defaultProps = {
  dataSelected: [],
  setDataSelected: () => {},
};

export default EnsayosModal;
